import { AlertTriangle, X } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface RecalculationConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  isPending?: boolean;
}

/**
 * FR-2/FR-3/FR-4 -- the KPS Configuration tab's "Apply new KPS configuration?" modal. Built
 * from the existing `AlertDialog` primitive, the same shape already used by
 * `settings/ai-configuration/VersionHistoryList.tsx`, `shared/ConfirmDeleteDialog.tsx` and
 * `ab-tests/ExitConfirmDialog.tsx` (`components/keyword.md § The confirmation modal`).
 *
 * Copy is verbatim from `design-input/kps-config-recalculation/sources.md`, screenshot
 * `01-confirm-modal.png`, except AUT-281 removed the "up to 20 minutes" duration claim: PR #259
 * made the run fast (~8.7s at ~600k rows, measured) and the twenty-minute assumption the estimate
 * rested on no longer holds. No replacement number is shown -- "under a minute at 6.6M" is a
 * projection, not a measurement, and putting an unmeasured number on screen is the defect this
 * ticket removes. The Snowflake-credits warning stays; it is about caution, not duration.
 * `Cancel` and `×` are the same outcome -- both are plain `AlertDialogCancel`s, which only
 * close the dialog; neither calls `onConfirm`, so no request is sent and the caller's
 * unsaved form state is untouched (FR-3).
 */
export function RecalculationConfirmDialog({
  open,
  onOpenChange,
  onConfirm,
  isPending = false,
}: RecalculationConfirmDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogCancel
          className="absolute right-4 top-4 h-7 w-7 rounded-md border border-border bg-card p-0 text-muted-foreground hover:bg-muted hover:text-foreground"
          aria-label="Close"
        >
          <X className="h-3.5 w-3.5" />
        </AlertDialogCancel>
        <AlertDialogHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 shrink-0 text-status-warning-strong" aria-hidden="true" />
            <AlertDialogTitle>Apply new KPS configuration?</AlertDialogTitle>
          </div>
          <AlertDialogDescription>
            Saving will apply these changes and recalculate the KPS for{" "}
            <strong className="font-semibold text-foreground">every keyword in Signal</strong>. This
            consumes a significant amount of Snowflake credits. Please be very discerning when
            making changes.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} disabled={isPending}>
            Save & recalculate
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
